import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Brain,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  RefreshCw,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { useAuth } from "../hooks/useAuth.js";
import {
  type KpiInsights,
  type KpiPerson,
  type KpiVergleichsZone,
  type KpiZonenKennzahlen,
  kpiApi,
} from "../lib/api.js";

// ── Month label helpers ───────────────────────────────────────────────────────

const MONTH_SHORT: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mrz",
  "04": "Apr",
  "05": "Mai",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Okt",
  "11": "Nov",
  "12": "Dez",
};

const MONTH_LONG: Record<string, string> = {
  "01": "Januar",
  "02": "Februar",
  "03": "März",
  "04": "April",
  "05": "Mai",
  "06": "Juni",
  "07": "Juli",
  "08": "August",
  "09": "September",
  "10": "Oktober",
  "11": "November",
  "12": "Dezember",
};

function fmtMonthShort(ym: string): string {
  // "2026-05" → "Mai 26"
  const [year, mm] = ym.split("-");
  return `${MONTH_SHORT[mm] ?? mm} ${year.slice(2)}`;
}

function fmtMonthLong(ym: string): string {
  // "2026-05" → "Mai 2026"
  const [year, mm] = ym.split("-");
  return `${MONTH_LONG[mm] ?? mm} ${year}`;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  valueColor?: string;
}

function KpiCard({ label, value, sub, valueColor }: KpiCardProps) {
  return (
    <div className="card px-4 py-4 flex flex-col gap-1" style={{ minWidth: 0 }}>
      <p
        className="text-[10px] font-medium tracking-widest uppercase"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </p>
      <p
        className="text-2xl font-bold font-mono-data leading-tight"
        style={{ color: valueColor ?? "var(--text-primary)" }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          {sub}
        </p>
      )}
    </div>
  );
}

// ── Severity helpers (KI-Panel) ───────────────────────────────────────────────

const SEVERITY_COLOR: Record<KpiInsights["insights"][number]["severity"], string> = {
  critical: "var(--risk-high-text)",
  warning: "var(--risk-medium-text)",
  info: "var(--text-muted)",
};

const SEVERITY_LABEL: Record<KpiInsights["insights"][number]["severity"], string> = {
  critical: "Kritisch",
  warning: "Warnung",
  info: "Info",
};

// ── Hover tooltip ─────────────────────────────────────────────────────────────

interface TooltipState {
  x: number;
  y: number;
  people: KpiPerson[];
}

interface HoverTooltipProps {
  state: TooltipState | null;
}

const MAX_TOOLTIP_NAMES = 15;

function HoverTooltip({ state }: HoverTooltipProps) {
  if (!state) return null;

  const { x, y, people } = state;
  if (people.length === 0) return null;

  const visible = people.slice(0, MAX_TOOLTIP_NAMES);
  const remaining = people.length - visible.length;

  // Nahe am unteren/rechten Rand nach oben/links klappen, damit nichts abgeschnitten wird
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1080;
  const flipUp = y > vh - 320;
  const flipLeft = x > vw - 320;

  return (
    <div
      style={{
        position: "fixed",
        left: flipLeft ? undefined : x + 12,
        right: flipLeft ? vw - x + 12 : undefined,
        top: flipUp ? undefined : y + 12,
        bottom: flipUp ? vh - y + 12 : undefined,
        zIndex: 9999,
        pointerEvents: "none",
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: "8px 12px",
        minWidth: 160,
        maxWidth: 280,
        maxHeight: "70vh",
        overflowY: "auto",
        boxShadow: "var(--shadow-md)",
      }}
    >
      {visible.map((p) => (
        <div
          key={`${p.employeeId}-${p.date}`}
          style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 3 }}
        >
          <span style={{ fontSize: 12, color: "var(--text-primary)", flexShrink: 1, minWidth: 0 }}>
            {p.name}
          </span>
          <span
            style={{
              fontSize: 10,
              color: "var(--text-muted)",
              background: "var(--bg-hover)",
              borderRadius: 4,
              padding: "1px 5px",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {p.bereich ?? p.department ?? "–"}
          </span>
        </div>
      ))}
      {remaining > 0 && (
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
          … und {remaining} weitere
        </div>
      )}
    </div>
  );
}

// ── Zeitraum-Presets ──────────────────────────────────────────────────────────

/** Monat "YYYY-MM" von heute n Monate zurück (n=0 = aktueller Monat). */
function monthsBack(n: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - n);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// `to` steuert das Fensterende: abgeschlossene Monate enden im Vormonat
// (monthsBack(1)); rollende Sichten (365 Tage / Jahresbeginn) gehen bis zum
// laufenden Monat (monthsBack(0)).
const LAST_COMPLETED = monthsBack(1);
const CUR_MONTH = monthsBack(0);

const TIMEFRAME_PRESETS: Array<{ key: string; label: string; from: string; to: string }> = [
  { key: "6m", label: "6 Monate", from: monthsBack(6), to: LAST_COMPLETED },
  { key: "12m", label: "12 Monate", from: monthsBack(12), to: LAST_COMPLETED },
  { key: "24m", label: "24 Monate", from: monthsBack(24), to: LAST_COMPLETED },
  { key: "36m", label: "36 Monate", from: monthsBack(36), to: LAST_COMPLETED },
  { key: "365d", label: "Letzte 365 Tage", from: monthsBack(11), to: CUR_MONTH },
  {
    key: "ytd",
    label: "Seit Jahresbeginn",
    from: `${new Date().getUTCFullYear()}-01`,
    to: CUR_MONTH,
  },
  { key: "2021", label: "Seit 2021", from: "2021-01", to: LAST_COMPLETED },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export function KpiReporting() {
  // Default: letzte 12 abgeschlossenen Monate
  const [tf, setTf] = useState<string>("12m");
  const preset = TIMEFRAME_PRESETS.find((p) => p.key === tf) ?? TIMEFRAME_PRESETS[1]!;
  // Der Aufriss steht in der URL (Vorbild Wirtschaftlichkeit.tsx): Zeilenklick
  // aus dem Segmentvergleich, die beiden Selects und ein geteilter Link
  // benutzen denselben Mechanismus statt drei getrennter Zustände.
  const [searchParams, setSearchParams] = useSearchParams();
  const bereich = searchParams.get("bereich");
  // Ohne Bereich ist ein Unterbereich sinnlos — ein Rest-Parameter in der URL
  // würde sonst unsichtbar weiterfiltern.
  const unterbereich = bereich !== null ? searchParams.get("unterbereich") : null;
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  /**
   * Segment (und optional Unterbereich) setzen. Der Monat wird dabei IMMER
   * zurückgesetzt — sonst bleibt ein Detailpanel des vorigen Bereichs stehen
   * und behauptet Namen, die zur neuen Auswahl nicht gehören.
   */
  function waehleBereich(code: string | null, sub?: string) {
    const next = new URLSearchParams(searchParams);
    if (code === null) next.delete("bereich");
    else next.set("bereich", code);
    if (code !== null && sub) next.set("unterbereich", sub);
    else next.delete("unterbereich");
    setSearchParams(next);
    setSelectedMonth(null);
  }

  function waehleUnterbereich(sub: string | null) {
    const next = new URLSearchParams(searchParams);
    if (sub === null) next.delete("unterbereich");
    else next.set("unterbereich", sub);
    setSearchParams(next);
    setSelectedMonth(null);
  }
  const { user } = useAuth();
  // Spiegelbild des Backend-Gates (canSeeKpiNames): kpi_viewer ist reine
  // Aggregat-Rolle — Monats-Detail (Klarnamen) wäre serverseitig 403.
  const canSeeNames = user != null && ["admin", "hrbp", "manager", "vorstand"].includes(user.role);
  const openMonth = (month: string) => {
    if (canSeeNames) setSelectedMonth(month);
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["kpi-reporting", preset.from, preset.to, bereich, unterbereich],
    queryFn: () =>
      kpiApi.getReport(preset.from, bereich ?? undefined, preset.to, unterbereich ?? undefined),
    staleTime: 5 * 60_000,
  });

  const { data: monthDetail, isLoading: monthDetailLoading } = useQuery({
    queryKey: ["kpi-month", selectedMonth, bereich, unterbereich],
    queryFn: () =>
      kpiApi.getMonthDetail(selectedMonth!, bereich ?? undefined, unterbereich ?? undefined),
    enabled: selectedMonth !== null,
    staleTime: 2 * 60_000,
  });

  const {
    data: insightsData,
    isLoading: insightsLoading,
    isError: insightsError,
  } = useQuery({
    queryKey: ["kpi-insights", bereich, unterbereich],
    queryFn: () => kpiApi.getInsights(bereich ?? undefined, unterbereich ?? undefined),
    staleTime: 10 * 60_000,
    enabled: true,
  });

  // Derived values
  const latest = data?.headcount.length ? data.headcount[data.headcount.length - 1] : null;

  const latestTerminations = data?.terminations.length
    ? data.terminations[data.terminations.length - 1]
    : null;

  const sumRegrettableConfirmed = data?.regrettable.reduce((acc, r) => acc + r.confirmed, 0) ?? 0;

  // Chart data: merge terminations into recharts-friendly format (Gesamt/Consulting/Intern)
  const chartData = (data?.terminations ?? []).map((t) => ({
    month: t.month,
    label: fmtMonthShort(t.month),
    total: t.total,
    consulting: t.consulting,
    intern: t.nonConsulting,
  }));

  // Trend chart data: multiply ratios by 100 for % display
  const trendChartData = (data?.fluctuationTrend ?? []).map((t) => ({
    label: fmtMonthShort(t.month),
    consulting: Math.round(t.consulting * 100),
    intern: Math.round(t.nonConsulting * 100),
    gesamt: Math.round(t.total * 100),
  }));

  // Build a lookup map for monthlyNames: month → names entry
  const namesIndex = new Map((data?.monthlyNames ?? []).map((mn) => [mn.month, mn]));

  // Auswahlliste des Unterbereichs-Filters inkl. eines gesetzten Werts, der in
  // den heutigen Stammdaten fehlt (siehe Kommentar am Feld).
  const unterbereichOptionen = useMemo(() => {
    if (bereich === null) return [];
    const ausStammdaten = data?.unterbereiche?.[bereich] ?? [];
    if (unterbereich === null || ausStammdaten.includes(unterbereich)) return ausStammdaten;
    return [...ausStammdaten, unterbereich];
  }, [bereich, unterbereich, data]);

  return (
    <AppLayout>
      <Header
        title="KPI Reporting"
        subtitle="Personalkennzahlen · Fluktuation · Kündigungsverlauf"
      />

      {/* Global hover tooltip — rendered at root so it floats above everything */}
      <HoverTooltip state={tooltip} />

      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* Controls row */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {/* Zeitraum-Presets */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {TIMEFRAME_PRESETS.map((p) => {
              const active = tf === p.key;
              return (
                <button
                  type="button"
                  key={p.key}
                  onClick={() => {
                    setTf(p.key);
                    setSelectedMonth(null);
                  }}
                  className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
                  style={{
                    background: active ? "var(--accent)" : "transparent",
                    color: active ? "#fff" : "var(--text-muted)",
                    border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Bereich selector — only show once bereiche are loaded */}
          {data?.bereiche && data.bereiche.length > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              <label
                htmlFor="kpi-bereich"
                className="text-[10px] font-medium tracking-widest uppercase"
                style={{ color: "var(--text-muted)" }}
              >
                Bereich
              </label>
              <select
                id="kpi-bereich"
                value={bereich ?? ""}
                onChange={(e) => waehleBereich(e.target.value || null)}
                className="bg-transparent text-xs flex-1 outline-none cursor-pointer"
                style={{ color: "var(--text-primary)", border: "none" }}
              >
                <option value="">Alle Bereiche</option>
                <optgroup label="Consulting">
                  {data.bereiche
                    .filter((b) => b.group === "consulting")
                    .map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.label}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Intern">
                  {data.bereiche
                    .filter((b) => b.group === "intern")
                    .map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.label}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          )}

          {/* W18: Unterbereich (3. Team-Ebene) — nur bei gewähltem CU-Bereich.
              Die Liste kommt aus den HEUTIGEN Stammdaten, der Aufriss aus der
              Vergleichssicht dagegen aus dem Zeitfenster: ein im Fenster
              aufgelöster Unterbereich fehlt dort. Deshalb wird der gesetzte
              Wert notfalls selbst als Option ergänzt — sonst zeigte das Feld
              „Alle Unterbereiche", während der Filter längst greift. */}
          {bereich !== null && unterbereichOptionen.length > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              <label
                htmlFor="kpi-unterbereich"
                className="text-[10px] font-medium tracking-widest uppercase"
                style={{ color: "var(--text-muted)" }}
              >
                Unterbereich
              </label>
              <select
                id="kpi-unterbereich"
                value={unterbereich ?? ""}
                onChange={(e) => waehleUnterbereich(e.target.value || null)}
                className="bg-transparent text-xs flex-1 outline-none cursor-pointer"
                style={{ color: "var(--text-primary)", border: "none" }}
              >
                <option value="">Alle Unterbereiche</option>
                {unterbereichOptionen.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-56 gap-3">
            <RefreshCw size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              KPI-Daten werden geladen…
            </p>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="card flex items-center gap-2 px-4 py-3 mb-4">
            <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error instanceof Error ? error.message : "KPI-Daten konnten nicht geladen werden."}
            </p>
          </div>
        )}

        {/* Data */}
        {!isLoading && !isError && data && (
          <>
            {/* KPI cards */}
            {latest && (
              <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                <KpiCard
                  label="Aktueller Bestand"
                  value={latest.total.bestand}
                  sub={`Consulting ${latest.consulting.bestand} · Intern ${latest.nonConsulting.bestand} · Verhältnis ${ratioLabel(latest.consulting.bestand, latest.nonConsulting.bestand)}`}
                />
                {/* Wachstum (Excel-Blatt "Wachstum"): Bestand über den gewählten Zeitraum */}
                <KpiCard
                  label={`Wachstum Consulting (seit ${fmtMonthShort(data.fromMonth)})`}
                  value={`${data.growth.consulting >= 0 ? "+" : ""}${Math.round(data.growth.consulting * 100)}%`}
                  valueColor={
                    data.growth.consulting >= 0 ? "var(--risk-low-text)" : "var(--risk-high-text)"
                  }
                  sub={`Intern ${data.growth.nonConsulting >= 0 ? "+" : ""}${Math.round(data.growth.nonConsulting * 100)}% · Gesamt ${data.growth.total >= 0 ? "+" : ""}${Math.round(data.growth.total * 100)}%`}
                />
                {/* 2a: Consulting als primäre Fluktuationszahl (folgt dem Zeitraum-Filter) */}
                <KpiCard
                  label={`Fluktuation Consulting ${data.months.length}M`}
                  value={`${Math.round(data.fluctuation.consulting * 100)}%`}
                  sub={`Intern ${Math.round(data.fluctuation.nonConsulting * 100)}% · Gesamt ${Math.round(data.fluctuation.total * 100)}%`}
                />
                <KpiCard
                  label={`Kündigungen ${data.months.length ? fmtMonthShort(data.months[data.months.length - 1]!) : ""}`}
                  value={latestTerminations?.total ?? 0}
                  valueColor={
                    (latestTerminations?.total ?? 0) > 0 ? "var(--risk-high-text)" : undefined
                  }
                />
                {/* 2b: Nur bestätigte Bedauerlich-Kündigungen, kein "geschätzt".
                    Ohne Sichtbarkeit „—" statt 0: die Summe fehlt, sie ist nicht null. */}
                <KpiCard
                  label={`Bedauerlich ${data.months.length}M`}
                  value={data.bedauerlichSichtbar ? sumRegrettableConfirmed : "—"}
                  sub={
                    data.bedauerlichSichtbar
                      ? "in Personio bestätigt"
                      : "für diese Rolle nicht sichtbar"
                  }
                />
              </div>
            )}

            {/* KI-Musteranalyse: ans Seitenende verschoben (siehe unten) */}

            {/* Kundensegmente im Vergleich (W19) — der Aufriss „alle Segmente →
                ein Segment → Unterbereich" liegt auf DIESER Seite, kein eigener
                Reiter. Mit gewähltem Segment weicht der Block dem Rücksprung. */}
            {bereich === null ? (
              <SegmentVergleichPanel from={preset.from} to={preset.to} onOpenZone={waehleBereich} />
            ) : (
              <div className="mb-5">
                <button
                  type="button"
                  onClick={() => waehleBereich(null)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                  style={{
                    color: "var(--accent)",
                    background: "transparent",
                    border: "1px solid var(--border)",
                    cursor: "pointer",
                  }}
                >
                  <ArrowLeft size={12} aria-hidden />
                  Alle Segmente vergleichen
                </button>
              </div>
            )}

            {/* Kündigungsverlauf */}
            <div className="card px-4 py-4 mb-5">
              <div className="flex items-center justify-between mb-3">
                <p
                  className="text-[10px] font-medium tracking-widest uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  Eingegangene Kündigungen
                </p>
                {data.terminationDataAvailable && chartData.length > 0 && canSeeNames && (
                  <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                    Auf einen Monat klicken für Details
                  </p>
                )}
              </div>
              {data.terminationDataAvailable && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                    onClick={(state) => {
                      if (state?.activePayload && state.activePayload.length > 0) {
                        const datum = state.activePayload[0].payload as {
                          month: string;
                          label: string;
                          total: number;
                        };
                        openMonth(datum.month);
                      }
                    }}
                    style={{ cursor: canSeeNames ? "pointer" : "default" }}
                  >
                    <CartesianGrid strokeDasharray="2 4" stroke="var(--chart-grid)" opacity={0.6} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                      width={28}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--bg-card)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: 12,
                        color: "var(--text-primary)",
                      }}
                      formatter={(v: number, name: string) => [v, name]}
                      labelFormatter={(l: string) => l}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: "var(--text-muted)" }} />
                    <Line
                      type="monotone"
                      dataKey="consulting"
                      name="Consulting"
                      stroke="var(--chart-1)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "var(--chart-1)", strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="intern"
                      name="Intern"
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                      dot={{ r: 2.5, fill: "var(--chart-2)", strokeWidth: 0 }}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Gesamt"
                      stroke="var(--chart-reference)"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div
                  className="flex items-center justify-center h-16"
                  style={{ color: "var(--text-muted)" }}
                >
                  <Users size={14} className="mr-2" />
                  <span className="text-sm">Kündigungsdaten im Aufbau</span>
                </div>
              )}
            </div>

            {/* 2d: Fluktuations-Trendlinie */}
            {trendChartData.length > 0 && (
              <div className="card px-4 py-4 mb-5">
                <p
                  className="text-[10px] font-medium tracking-widest uppercase mb-3"
                  style={{ color: "var(--text-muted)" }}
                >
                  Fluktuations-Trend (rollend 12M)
                </p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={trendChartData}
                    margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="2 4" stroke="var(--chart-grid)" opacity={0.6} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                      width={36}
                      tickFormatter={(v: number) => `${v}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--bg-card)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: 12,
                        color: "var(--text-primary)",
                      }}
                      formatter={(v: number, name: string) => [`${v}%`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: "var(--text-muted)" }} />
                    <Line
                      type="monotone"
                      dataKey="consulting"
                      name="Consulting"
                      stroke="var(--chart-1)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "var(--chart-1)", strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="intern"
                      name="Intern"
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                      dot={{ r: 2.5, fill: "var(--chart-2)", strokeWidth: 0 }}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="gesamt"
                      name="Gesamt"
                      stroke="var(--chart-reference)"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Personalakquise-Trichter (Greenhouse) — nur in der Gesamt-Ansicht
                (eigene Segmentierung TNW/Senior/Intern, hängt nicht am Personio-Bereich) */}
            {bereich === null && <RecruitingPanel />}

            {/* Month detail panel */}
            {selectedMonth !== null && (
              <div className="card px-4 py-4 mb-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                    {fmtMonthLong(selectedMonth)}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedMonth(null)}
                    className="flex items-center justify-center rounded"
                    style={{
                      color: "var(--text-muted)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px",
                    }}
                    aria-label="Schließen"
                  >
                    <X size={14} />
                  </button>
                </div>

                {monthDetailLoading ? (
                  <div
                    className="flex items-center gap-2 py-4"
                    style={{ color: "var(--text-muted)" }}
                  >
                    <RefreshCw size={13} className="animate-spin" />
                    <span className="text-xs">Wird geladen…</span>
                  </div>
                ) : monthDetail ? (
                  <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                    {/* Eintritte column — first */}
                    <div>
                      <p
                        className="text-[10px] font-medium tracking-widest uppercase mb-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Eintritte ({monthDetail.eintritte.length})
                      </p>
                      {monthDetail.eintritte.length === 0 ? (
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                          keine
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {monthDetail.eintritte.map((p) => (
                            <li key={p.employeeId} className="flex items-baseline gap-2">
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                {p.name}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: "var(--bg-hover)",
                                  color: "var(--text-muted)",
                                  flexShrink: 0,
                                }}
                              >
                                {p.bereich ?? p.department ?? "–"}
                              </span>
                              <span
                                className="text-[10px]"
                                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                              >
                                {p.date}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Kündigungen column */}
                    <div>
                      <p
                        className="text-[10px] font-medium tracking-widest uppercase mb-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Eingegangene Kündigungen ({monthDetail.kuendigungen.length})
                      </p>
                      {monthDetail.kuendigungen.length === 0 ? (
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                          keine
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {monthDetail.kuendigungen.map((p) => (
                            <li key={p.employeeId} className="flex items-baseline gap-2">
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                {p.name}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: "var(--bg-hover)",
                                  color: "var(--text-muted)",
                                  flexShrink: 0,
                                }}
                              >
                                {p.bereich ?? p.department ?? "–"}
                              </span>
                              <span
                                className="text-[10px]"
                                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                              >
                                {p.date}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Austritte column */}
                    <div>
                      <p
                        className="text-[10px] font-medium tracking-widest uppercase mb-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Austritte ({monthDetail.austritte.length})
                      </p>
                      {monthDetail.austritte.length === 0 ? (
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                          keine
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {monthDetail.austritte.map((p) => (
                            <li key={p.employeeId} className="flex items-baseline gap-2">
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                {p.name}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: "var(--bg-hover)",
                                  color: "var(--text-muted)",
                                  flexShrink: 0,
                                }}
                              >
                                {p.bereich ?? p.department ?? "–"}
                              </span>
                              <span
                                className="text-[10px]"
                                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                              >
                                {p.date}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : null}
                {bereich !== null && monthDetail && (
                  <div className="grid gap-5 mt-5" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                    <div>
                      <p
                        className="text-[10px] font-medium tracking-widest uppercase mb-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Interne Zugänge ({monthDetail.interneZugaenge.length})
                      </p>
                      {monthDetail.interneZugaenge.length === 0 ? (
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                          keine
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {monthDetail.interneZugaenge.map((w) => (
                            <li
                              key={`${w.employeeId}-${w.date}`}
                              className="flex items-baseline gap-2"
                            >
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                {w.name}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: "var(--bg-hover)",
                                  color: "var(--text-muted)",
                                  flexShrink: 0,
                                }}
                              >
                                {w.von} → {w.nach}
                              </span>
                              <span
                                className="text-[10px]"
                                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                              >
                                {w.date}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <p
                        className="text-[10px] font-medium tracking-widest uppercase mb-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        Interne Abgänge ({monthDetail.interneAbgaenge.length})
                      </p>
                      {monthDetail.interneAbgaenge.length === 0 ? (
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                          keine
                        </p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {monthDetail.interneAbgaenge.map((w) => (
                            <li
                              key={`${w.employeeId}-${w.date}`}
                              className="flex items-baseline gap-2"
                            >
                              <span className="text-sm" style={{ color: "var(--text-primary)" }}>
                                {w.name}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{
                                  background: "var(--bg-hover)",
                                  color: "var(--text-muted)",
                                  flexShrink: 0,
                                }}
                              >
                                {w.von} → {w.nach}
                              </span>
                              <span
                                className="text-[10px]"
                                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                              >
                                {w.date}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div />
                  </div>
                )}
              </div>
            )}

            {/* Monats-Matrix table */}
            <div className="card overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-2.5"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <p
                  className="text-[10px] font-medium tracking-widest uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  Monats-Matrix
                </p>
                {/* 2c: hint for month click */}
                <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  Monat anklicken für Namen · Zellen-Hover für Personen
                </p>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 11,
                    minWidth: 600,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "var(--bg-card)",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <th
                        style={{
                          textAlign: "left",
                          padding: "6px 12px",
                          color: "var(--text-muted)",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          background: "var(--bg-card)",
                          zIndex: 1,
                          minWidth: 160,
                        }}
                      >
                        Kennzahl
                      </th>
                      {/* 2c: clickable month header cells */}
                      {data.months.map((m) => (
                        <th
                          key={m}
                          onClick={() => openMonth(m)}
                          style={{
                            textAlign: "right",
                            padding: "6px 8px",
                            color: selectedMonth === m ? "var(--accent)" : "var(--text-muted)",
                            fontWeight: selectedMonth === m ? 700 : 500,
                            whiteSpace: "nowrap",
                            cursor: canSeeNames ? "pointer" : "default",
                            userSelect: "none",
                            transition: "color 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            if (selectedMonth !== m) {
                              (e.currentTarget as HTMLTableCellElement).style.color =
                                "var(--text-primary)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedMonth !== m) {
                              (e.currentTarget as HTMLTableCellElement).style.color =
                                "var(--text-muted)";
                            }
                          }}
                        >
                          {fmtMonthShort(m)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Consulting/Intern-Split nur in der Gesamt-Ansicht — in der
                        Bereichssicht ist er redundant (ein Bereich liegt komplett auf
                        einer Seite) und würde bei Consulting↔Intern-Wechslern nach dem
                        heutigen Department sortieren (Review-Finding I-1). */}
                    {data.bereich === null && (
                      <MatrixSectionRows
                        label="Consulting"
                        months={data.months}
                        onTooltip={setTooltip}
                        rows={[
                          {
                            key: "Bestand",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.consulting.bestand ??
                                null,
                            ),
                          },
                          {
                            key: "Zugänge",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.consulting.zugaenge ??
                                null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.eintritte ?? []).filter(
                                (p) => p.department === "Consulting",
                              ),
                          },
                          {
                            key: "Abgänge",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.consulting.abgaenge ??
                                null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.abgaenge ?? []).filter(
                                (p) => p.department === "Consulting",
                              ),
                          },
                        ]}
                      />
                    )}
                    {data.bereich === null && (
                      <MatrixSectionRows
                        label="Non-Consulting"
                        months={data.months}
                        onTooltip={setTooltip}
                        rows={[
                          {
                            key: "Bestand",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.nonConsulting.bestand ??
                                null,
                            ),
                          },
                          {
                            key: "Zugänge",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.nonConsulting.zugaenge ??
                                null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.eintritte ?? []).filter(
                                (p) => p.department !== "Consulting",
                              ),
                          },
                          {
                            key: "Abgänge",
                            values: data.months.map(
                              (m) =>
                                data.headcount.find((h) => h.month === m)?.nonConsulting.abgaenge ??
                                null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.abgaenge ?? []).filter(
                                (p) => p.department !== "Consulting",
                              ),
                          },
                        ]}
                      />
                    )}
                    <MatrixSectionRows
                      label="Gesamt"
                      months={data.months}
                      onTooltip={setTooltip}
                      rows={[
                        {
                          key: "Bestand",
                          values: data.months.map(
                            (m) => data.headcount.find((h) => h.month === m)?.total.bestand ?? null,
                          ),
                        },
                        {
                          key: "Zugänge",
                          values: data.months.map(
                            (m) =>
                              data.headcount.find((h) => h.month === m)?.total.zugaenge ?? null,
                          ),
                          namesByMonth: (m) => namesIndex.get(m)?.eintritte ?? [],
                        },
                        {
                          key: "Abgänge",
                          values: data.months.map(
                            (m) =>
                              data.headcount.find((h) => h.month === m)?.total.abgaenge ?? null,
                          ),
                          namesByMonth: (m) => namesIndex.get(m)?.abgaenge ?? [],
                        },
                        ...(data.bereich === null
                          ? [
                              {
                                key: "Verhältnis Consulting/Intern",
                                values: data.months.map((m) => {
                                  const h = data.headcount.find((x) => x.month === m);
                                  return h
                                    ? ratioLabel(h.consulting.bestand, h.nonConsulting.bestand)
                                    : null;
                                }),
                              },
                            ]
                          : []),
                      ]}
                    />
                    {/* F2: interne Bereichswechsel — nur bei aktivem Bereichs-Filter */}
                    {data.bereich !== null && (
                      <MatrixSectionRows
                        label="Interne Wechsel"
                        months={data.months}
                        onTooltip={setTooltip}
                        rows={[
                          {
                            key: "Zugänge aus anderen Bereichen",
                            values: data.months.map(
                              (m) =>
                                data.interneWechsel.find((w) => w.month === m)?.zugaenge ?? null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.interneZugaenge ?? []).map((w) => ({
                                employeeId: w.employeeId,
                                name: w.name,
                                department: null,
                                bereich: `${w.von} → ${w.nach}`,
                                date: w.date,
                              })),
                          },
                          {
                            key: "Abgänge in andere Bereiche",
                            values: data.months.map(
                              (m) =>
                                data.interneWechsel.find((w) => w.month === m)?.abgaenge ?? null,
                            ),
                            namesByMonth: (m) =>
                              (namesIndex.get(m)?.interneAbgaenge ?? []).map((w) => ({
                                employeeId: w.employeeId,
                                name: w.name,
                                department: null,
                                bereich: `${w.von} → ${w.nach}`,
                                date: w.date,
                              })),
                          },
                        ]}
                      />
                    )}
                    {data.terminationDataAvailable && (
                      <MatrixSectionRows
                        label="Kündigungen"
                        months={data.months}
                        onTooltip={setTooltip}
                        rows={[
                          {
                            key: "eingegangene",
                            values: data.months.map(
                              (m) => data.terminations.find((t) => t.month === m)?.total ?? null,
                            ),
                            namesByMonth: (m) => namesIndex.get(m)?.kuendigungen ?? [],
                          },
                          ...(data.bereich === null
                            ? [
                                {
                                  key: "· Consulting",
                                  values: data.months.map(
                                    (m) =>
                                      data.terminations.find((t) => t.month === m)?.consulting ??
                                      null,
                                  ),
                                  namesByMonth: (m: string) =>
                                    (namesIndex.get(m)?.kuendigungen ?? []).filter(
                                      (p) => p.department === "Consulting",
                                    ),
                                },
                                {
                                  key: "· Intern",
                                  values: data.months.map(
                                    (m) =>
                                      data.terminations.find((t) => t.month === m)?.nonConsulting ??
                                      null,
                                  ),
                                  namesByMonth: (m: string) =>
                                    (namesIndex.get(m)?.kuendigungen ?? []).filter(
                                      (p) => p.department !== "Consulting",
                                    ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                    {/* 2b: Bedauerlich — nur bestätigt, kein geschätzt; und nur
                        für Rollen, die die Bewertung sehen dürfen. */}
                    {data.terminationDataAvailable && data.bedauerlichSichtbar && (
                      <MatrixSectionRows
                        label="Bedauerlich"
                        months={data.months}
                        onTooltip={setTooltip}
                        rows={[
                          {
                            key: "bestätigt",
                            values: data.months.map(
                              (m) => data.regrettable.find((r) => r.month === m)?.confirmed ?? null,
                            ),
                            namesByMonth: (m) => namesIndex.get(m)?.bedauerlich ?? [],
                          },
                          ...(data.bereich === null
                            ? [
                                {
                                  key: "· Consulting",
                                  values: data.months.map(
                                    (m) =>
                                      data.regrettable.find((r) => r.month === m)?.consulting ??
                                      null,
                                  ),
                                  namesByMonth: (m: string) =>
                                    (namesIndex.get(m)?.bedauerlich ?? []).filter(
                                      (p) => p.department === "Consulting",
                                    ),
                                },
                                {
                                  key: "· Intern",
                                  values: data.months.map(
                                    (m) =>
                                      data.regrettable.find((r) => r.month === m)?.nonConsulting ??
                                      null,
                                  ),
                                  namesByMonth: (m: string) =>
                                    (namesIndex.get(m)?.bedauerlich ?? []).filter(
                                      (p) => p.department !== "Consulting",
                                    ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </tbody>
                </table>
              </div>
              {/* Stichtags-Hinweis: erklärt den Monats-Versatz zwischen Abgangs-Zahl
                  und Bestand (letzter Arbeitstag = an dem Tag noch beschäftigt). */}
              <p className="text-[11px] px-4 py-2" style={{ color: "var(--text-muted)" }}>
                Bestand = Beschäftigte am Monatsende. Wer zum Monatsletzten austritt, zählt an
                diesem Stichtag noch mit — der Bestand sinkt dadurch erst im Folgemonat.
              </p>
            </div>

            {/* KI-Musteranalyse — ganz am Seitenende, ohne Maßnahmen-Empfehlungen
                (KPI Reporting sehen alle FK). */}
            <div className="card p-5 mt-5">
              <div className="flex items-center gap-2 mb-4">
                <Brain size={15} style={{ color: "var(--accent)" }} />
                <h2 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                  KI-Musteranalyse
                </h2>
              </div>

              {insightsLoading && (
                <div className="flex items-center gap-2 py-6 justify-center">
                  <Loader2 size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    KI analysiert die Entwicklung…
                  </span>
                </div>
              )}

              {insightsError && !insightsLoading && (
                <div
                  className="flex items-center gap-2 py-4 px-3 rounded"
                  style={{ background: "var(--risk-high-bg)" }}
                >
                  <AlertTriangle size={14} style={{ color: "var(--risk-high)" }} />
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    KI-Analyse nicht verfügbar.
                  </span>
                </div>
              )}

              {insightsData && (
                <>
                  <p
                    className="text-xs mb-4 leading-relaxed"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {insightsData.summary}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {insightsData.insights.map((insight) => {
                      const color = SEVERITY_COLOR[insight.severity];
                      return (
                        <div
                          key={insight.id}
                          className="rounded p-4"
                          style={{
                            background: `color-mix(in srgb, ${color} 3%, transparent)`,
                            border: `1px solid color-mix(in srgb, ${color} 19%, transparent)`,
                          }}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold" style={{ color }}>
                              {insight.title}
                            </span>
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{
                                background: `color-mix(in srgb, ${color} 13%, transparent)`,
                                color,
                              }}
                            >
                              {SEVERITY_LABEL[insight.severity]}
                            </span>
                          </div>
                          <p
                            className="text-xs leading-relaxed"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {insight.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

// ── Matrix Section Rows ───────────────────────────────────────────────────────

interface MatrixRow {
  key: string;
  values: (number | string | null)[];
  /** When defined, this row's value cells show a hover tooltip listing names for that month. */
  namesByMonth?: (month: string) => KpiPerson[];
}

/** Verhältnis Consulting je Internem, Format "3,1:1" (X Berater auf 1 Internen). */
function ratioLabel(consulting: number, intern: number): string {
  if (intern <= 0) return "–";
  return `${(consulting / intern).toFixed(1).replace(".", ",")}:1`;
}

interface MatrixSectionRowsProps {
  label: string;
  rows: MatrixRow[];
  months: string[];
  onTooltip: (state: TooltipState | null) => void;
}

function MatrixSectionRows({ label, rows, months, onTooltip }: MatrixSectionRowsProps) {
  return (
    <>
      {/* Section header */}
      <tr
        style={{
          background: "var(--bg-card)",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <td
          colSpan={999}
          style={{
            padding: "4px 12px",
            color: "var(--text-muted)",
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            position: "sticky",
            left: 0,
            background: "var(--bg-card)",
          }}
        >
          {label}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.key} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
          <td
            style={{
              padding: "5px 12px 5px 20px",
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
              position: "sticky",
              left: 0,
              background: "inherit",
            }}
          >
            {row.key}
          </td>
          {row.values.map((v, i) => {
            const month = months[i];
            const hasTooltip = row.namesByMonth !== undefined;
            const resolver = row.namesByMonth;
            return (
              <td
                key={i}
                className="font-mono-data"
                style={{
                  textAlign: "right",
                  padding: "5px 8px",
                  color: v === null ? "var(--text-muted)" : "var(--text-primary)",
                  cursor: hasTooltip ? "help" : undefined,
                }}
                onMouseEnter={
                  hasTooltip && resolver
                    ? (e) => {
                        const people = resolver(month);
                        if (people.length > 0) {
                          const rect = (
                            e.currentTarget as HTMLTableCellElement
                          ).getBoundingClientRect();
                          onTooltip({
                            x: rect.left,
                            y: rect.top,
                            people,
                          });
                        }
                      }
                    : undefined
                }
                onMouseLeave={hasTooltip ? () => onTooltip(null) : undefined}
              >
                {v === null ? "—" : v}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ── Kundensegmente im Vergleich (W19) ─────────────────────────────────────────

/**
 * Kurzformen, die Timm im Gespräch benutzt. Reine ANZEIGE — die Wahrheit ist
 * der Rohname aus dem Personio-Teamnamen, er steht im title. Es wird nirgends
 * auf ein Kürzel verzweigt oder danach gefiltert: die Unterbereiche kommen
 * datengetrieben aus der API, ein hier fehlender Eintrag fällt auf den
 * Rohnamen zurück.
 */
const UNTERBEREICH_KURZ: Record<string, string> = {
  Projektumsetzung: "PU",
  "Management- und Technologieberatung": "MTB",
};

type SortKey =
  | "label"
  | "bestand"
  | "wachstum"
  | "zugaenge"
  | "abgaenge"
  | "intern"
  | "fluktuation"
  | "bedauerlich";

interface SpalteDef {
  key: SortKey;
  label: string;
  /** Spaltenkopf-Tooltip: Einheit UND Bezug — eine nackte Zahl ist nicht zitierfähig. */
  hilfe: string;
}

const SPALTE_ZONE: SpalteDef = {
  key: "label",
  label: "Segment",
  hilfe: "Kundensegment aus dem Personio-Teamnamen (CU - Segment - Unterbereich)",
};

const SPALTEN: readonly SpalteDef[] = [
  {
    key: "bestand",
    label: "Bestand",
    hilfe: "Beschäftigte am Ende des Zeitraums, in Personen",
  },
  {
    key: "wachstum",
    label: "Wachstum",
    hilfe: "Bestand am Ende gegen den Bestand vor dem Zeitraum, in Prozent",
  },
  { key: "zugaenge", label: "Zugänge", hilfe: "Eintritte im Zeitraum, in Personen" },
  { key: "abgaenge", label: "Abgänge", hilfe: "Austritte im Zeitraum, in Personen" },
  {
    key: "intern",
    label: "intern ±",
    hilfe: "Bereichswechsel: zugewechselte minus abgewechselte Personen",
  },
  {
    key: "fluktuation",
    label: "Fluktuation",
    hilfe:
      "Abgänge geteilt durch den Ø Bestand des Zeitraums, in Prozent; darunter die Abweichung zum Gesamtwert in Prozentpunkten",
  },
  {
    key: "bedauerlich",
    label: "Bedauerlich",
    hilfe: "In Personio als bedauerlich bestätigte Austritte des Zeitraums, in Personen",
  },
];

/** Raten-Spalten: Zeilen ohne belastbare Basis sortieren hier ans Ende. */
const RATEN_SPALTEN: ReadonlySet<SortKey> = new Set<SortKey>(["wachstum", "fluktuation"]);

const GRID_SEGMENTE = "minmax(190px, 1.6fr) 104px 92px 74px 74px 76px 112px 92px";
/** Summe der festen Spalten + Abstände + Innenabstand der Karte. */
const GRID_MIN_BREITE = 190 + 104 + 92 + 74 + 74 + 76 + 112 + 92 + 7 * 12 + 32;

/**
 * Einheitliches Minus (U+2212) statt des ASCII-Bindestrichs aus toFixed —
 * sonst stehen „-20,0 %" und „−11,1 pp" nebeneinander in derselben Zeile.
 */
function minus(s: string): string {
  return s.replace("-", "−");
}

/** Anteile kommen als 0–1 aus der API; null bleibt „—", nie 0 (0 % wäre eine Aussage). */
function fmtQuote(v: number | null): string {
  return v === null ? "—" : `${minus((v * 100).toFixed(1).replace(".", ","))} %`;
}

function fmtWachstum(v: number | null): string {
  if (v === null) return "—";
  const pct = v * 100;
  return `${pct > 0 ? "+" : ""}${minus(pct.toFixed(1).replace(".", ","))} %`;
}

function fmtVorzeichen(v: number): string {
  return v > 0 ? `+${v}` : minus(String(v));
}

function fmtEinsNach(v: number): string {
  return v.toFixed(1).replace(".", ",");
}

/**
 * Abweichung der Fluktuation gegen den Gesamtwert in Prozentpunkten. Bewusst
 * NICHT TrendBadge: dessen Schwellen und Farbsemantik hängen an der
 * Drop-Risiko-Domäne und wären hier schlicht falsch. Die Farbe steht nie
 * allein — Pfeil und Vorzeichen tragen dieselbe Aussage.
 */
function DeltaPp({ wert, referenz }: { wert: number | null; referenz: number | null }) {
  if (wert === null || referenz === null) return null;
  const pp = (wert - referenz) * 100;
  // Unter 0,05 pp ist es Rundungsrauschen; „±0,0 pp" sagt nichts und lenkt ab.
  if (Math.abs(pp) < 0.05) return null;
  const hoeher = pp > 0;
  const Pfeil = hoeher ? ArrowUp : ArrowDown;
  // Das Vorzeichen steht im TEXT, nicht nur im Pfeil: der ist aria-hidden, und
  // ohne Vorzeichen liest sich eine um 11 pp NIEDRIGERE Fluktuation wie eine
  // um 11 pp höhere. Farbe und Pfeil verstärken nur, sie tragen nichts allein.
  const vorzeichen = hoeher ? "+" : "−";
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-mono-data"
      style={{ color: hoeher ? "var(--risk-high-text)" : "var(--risk-low-text)" }}
      title={`${vorzeichen}${fmtEinsNach(Math.abs(pp))} Prozentpunkte gegenüber der Gesamtfluktuation`}
    >
      <Pfeil size={9} aria-hidden />
      {vorzeichen}
      {fmtEinsNach(Math.abs(pp))} pp
    </span>
  );
}

interface ZeilenKontext {
  gesamt: KpiZonenKennzahlen;
  maxBestand: number;
  minBasis: number;
  orgHistorieVerfuegbar: boolean;
  terminationDataAvailable: boolean;
  toMonth: string;
}

/** Die sieben Zahlenspalten einer Zeile — identisch für Zonen und Gesamtzeile. */
function KennzahlZellen({ k, ctx }: { k: KpiZonenKennzahlen; ctx: ZeilenKontext }) {
  const kleineBasis = !k.rateBelastbar;
  const basisHinweis = `Ø Bestand ${fmtEinsNach(k.bestandAvg)} liegt unter ${ctx.minBasis} — eine Quote hinge hier an einem einzelnen Austritt.`;
  const wachstumHinweis =
    k.bestandStart === 0 && k.ersterMonatMitBestand !== null
      ? `Kein Bestand vor dem Zeitraum — erster Monat mit Bestand: ${fmtMonthLong(k.ersterMonatMitBestand)}`
      : `Bestand ${k.bestandStart} vor dem Zeitraum → ${k.bestand} am Ende`;
  return (
    <>
      {/* Bestand: neutraler Proportionalbalken (--accent), keine Statusfarbe */}
      <span
        className="flex items-center justify-end gap-2"
        title={`${k.bestand} Personen am Ende von ${fmtMonthLong(ctx.toMonth)} · ${k.bestandStart} vor dem Zeitraum · Δ ${fmtVorzeichen(k.bestand - k.bestandStart)}`}
      >
        <span
          aria-hidden
          className="inline-block rounded shrink-0 overflow-hidden"
          style={{ width: 32, height: 5, background: "var(--bg-inset)" }}
        >
          <span
            className="block h-full rounded"
            style={{
              width: `${Math.min(100, Math.round((k.bestand / ctx.maxBestand) * 100))}%`,
              background: "var(--accent)",
            }}
          />
        </span>
        <span
          className="font-mono-data text-xs tabular-nums"
          style={{ color: "var(--text-primary)" }}
        >
          {k.bestand}
        </span>
      </span>

      <span
        className="font-mono-data text-xs text-right tabular-nums"
        title={kleineBasis ? basisHinweis : wachstumHinweis}
        style={{
          color: k.wachstum === null ? "var(--text-muted)" : "var(--text-primary)",
        }}
      >
        {fmtWachstum(k.wachstum)}
      </span>

      <span
        className="font-mono-data text-xs text-right tabular-nums"
        title={`${k.zugaenge} Eintritte im Zeitraum`}
        style={{ color: "var(--text-secondary)" }}
      >
        {k.zugaenge}
      </span>

      <span
        className="font-mono-data text-xs text-right tabular-nums"
        title={`${k.abgaenge} Austritte im Zeitraum`}
        style={{ color: "var(--text-secondary)" }}
      >
        {k.abgaenge}
      </span>

      {/* Interne Wechsel: ohne Org-Historie gibt es keine — dann „—" statt 0,
          weil 0 eine Aussage wäre, die die Datenlage nicht hergibt. */}
      <span
        className="font-mono-data text-xs text-right tabular-nums"
        title={
          ctx.orgHistorieVerfuegbar
            ? `${k.interneZugaenge} zugewechselt / ${k.interneAbgaenge} abgewechselt`
            : "Ohne Organisations-Historie werden interne Wechsel nicht ausgewiesen."
        }
        style={{
          color: ctx.orgHistorieVerfuegbar ? "var(--text-secondary)" : "var(--text-muted)",
        }}
      >
        {ctx.orgHistorieVerfuegbar ? fmtVorzeichen(k.interneZugaenge - k.interneAbgaenge) : "—"}
      </span>

      <span
        className="flex flex-col items-end leading-tight"
        title={
          kleineBasis
            ? basisHinweis
            : `${k.abgaenge} Abgänge auf Ø ${fmtEinsNach(k.bestandAvg)} Beschäftigte im Zeitraum`
        }
      >
        <span
          className="font-mono-data text-xs tabular-nums"
          style={{ color: k.fluktuation === null ? "var(--text-muted)" : "var(--text-primary)" }}
        >
          {fmtQuote(k.fluktuation)}
        </span>
        <DeltaPp wert={k.fluktuation} referenz={ctx.gesamt.fluktuation} />
      </span>

      {/* Bedauerlich ist eine Bewertung, keine Stammdatentatsache: Rollen ohne
          Personenbezug bekommen sie als null und sehen hier „—" wie bei
          fehlenden Kündigungsdaten. 0 stünde für „niemand" und wäre gelogen. */}
      <span
        className="font-mono-data text-xs text-right tabular-nums"
        title={
          k.bedauerlich === null
            ? ctx.terminationDataAvailable
              ? "Bedauerliche Austritte sind eine Bewertung einzelner Personen und für diese Rolle nicht sichtbar."
              : "Kündigungsdaten im Aufbau — bedauerliche Austritte werden noch nicht ausgewiesen."
            : `${k.bedauerlich} von ${k.abgaenge} Abgängen in Personio als bedauerlich bestätigt`
        }
        style={{
          color: k.bedauerlich === null ? "var(--text-muted)" : "var(--text-secondary)",
        }}
      >
        {ctx.terminationDataAvailable && k.bedauerlich !== null ? k.bedauerlich : "—"}
      </span>
    </>
  );
}

function zonenWert(z: KpiVergleichsZone, key: SortKey): number {
  const k = z.kennzahlen;
  switch (key) {
    case "bestand":
      return k.bestand;
    case "wachstum":
      return k.wachstum ?? 0;
    case "zugaenge":
      return k.zugaenge;
    case "abgaenge":
      return k.abgaenge;
    case "intern":
      return k.interneZugaenge - k.interneAbgaenge;
    case "fluktuation":
      return k.fluktuation ?? 0;
    case "bedauerlich":
      return k.bedauerlich ?? 0;
    default:
      return 0;
  }
}

/**
 * Sortiert eine Gruppe. Zeilen ohne belastbare Basis wandern bei Raten-
 * Sortierung ans Ende — sonst besetzt ein Zwei-Personen-Segment mit einem
 * einzigen Austritt dauerhaft Platz 1 der Ausreißerliste. Zweitschlüssel ist
 * immer das Label, damit die Reihenfolge bei Gleichstand reproduzierbar ist.
 */
function sortiereZonen(
  liste: KpiVergleichsZone[],
  key: SortKey,
  dir: "asc" | "desc",
): KpiVergleichsZone[] {
  const raten = RATEN_SPALTEN.has(key);
  return [...liste].sort((a, b) => {
    if (raten) {
      const ra = a.kennzahlen.rateBelastbar ? 0 : 1;
      const rb = b.kennzahlen.rateBelastbar ? 0 : 1;
      if (ra !== rb) return ra - rb;
    }
    if (key === "label") {
      const l = a.label.localeCompare(b.label, "de");
      return dir === "desc" ? -l : l;
    }
    const d = zonenWert(a, key) - zonenWert(b, key);
    if (d !== 0) return dir === "desc" ? -d : d;
    return a.label.localeCompare(b.label, "de");
  });
}

/**
 * Kindzeilen folgen derselben Sortierung wie die Wurzeln — sonst stünde unter
 * einer nach Bestand sortierten Tabelle eine unsortierte Gruppe. „ohne
 * Unterbereich" und die Sammelzeile bleiben dabei am Ende: sie sind keine
 * Unterbereiche, sondern die Erklärung, warum sich die Kinder zur Elternzeile
 * addieren, und gehören ans Ende der Aufzählung.
 */
function sortiereKinder(
  kinder: KpiVergleichsZone[],
  key: SortKey,
  dir: "asc" | "desc",
): KpiVergleichsZone[] {
  const echte = kinder.filter((k) => k.art === "unterbereich");
  const rest = kinder.filter((k) => k.art !== "unterbereich");
  return [...sortiereZonen(echte, key, dir), ...rest];
}

function SpaltenKopf({
  spalte,
  aktiv,
  dir,
  links,
  onSort,
}: {
  spalte: SpalteDef;
  aktiv: boolean;
  dir: "asc" | "desc";
  links?: boolean;
  onSort: (key: SortKey) => void;
}) {
  const Pfeil = dir === "desc" ? ArrowDown : ArrowUp;
  const richtung = dir === "desc" ? "aktuell absteigend" : "aktuell aufsteigend";
  return (
    <button
      type="button"
      onClick={() => onSort(spalte.key)}
      title={spalte.hilfe}
      aria-label={
        aktiv ? `${spalte.label} sortieren, ${richtung}` : `Nach ${spalte.label} sortieren`
      }
      className={`inline-flex items-center gap-1 text-xs font-medium ${links ? "justify-start" : "justify-end"}`}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: aktiv ? "var(--text-primary)" : "var(--text-muted)",
      }}
    >
      {links && aktiv && <Pfeil size={10} aria-hidden />}
      <span className="truncate">{spalte.label}</span>
      {!links && aktiv && <Pfeil size={10} aria-hidden />}
    </button>
  );
}

function ZonenZeile({
  zone,
  ctx,
  kind,
  offen,
  onToggle,
  onOpen,
}: {
  zone: KpiVergleichsZone;
  ctx: ZeilenKontext;
  kind: boolean;
  offen: boolean;
  onToggle: (code: string) => void;
  onOpen: (code: string, sub?: string) => void;
}) {
  // NUR bei art "unterbereich" trägt key.unterbereich einen echten Namen: in
  // der Sammelzeile steht dort ein Sentinel ("(weitere)"), der niemals als
  // Beschriftung durchschlagen darf — sonst liest der Vorstand einen
  // Platzhalter statt „Weitere Unterbereiche".
  const roh = zone.art === "unterbereich" ? zone.key.unterbereich : null;
  // Kürzel nur anzeigen, Rohname im title — nie umgekehrt.
  const anzeige = roh !== null ? (UNTERBEREICH_KURZ[roh] ?? roh) : zone.label;
  // Nur echte Organisationseinheiten haben eine Einzelsicht; Resteimer und
  // Sammelzeile lassen sich nicht als ?bereich= ausdrücken.
  const klickbar = zone.art === "segment" || zone.art === "unterbereich";
  const titel = roh !== null ? `${roh} — Einzelsicht öffnen` : `${zone.label} — Einzelsicht öffnen`;
  return (
    <div
      className="grid items-center px-4 py-2 row-hover"
      style={{
        gridTemplateColumns: GRID_SEGMENTE,
        gap: "0 12px",
        borderBottom: "1px solid var(--border)",
        background: kind ? "var(--bg-subtle)" : "transparent",
      }}
    >
      <span className="flex items-center gap-1 min-w-0" style={{ paddingLeft: kind ? 20 : 0 }}>
        {zone.hatUnterbereiche ? (
          <button
            type="button"
            onClick={() => onToggle(zone.key.segment)}
            aria-expanded={offen}
            aria-label={`Unterbereiche von ${zone.label} ${offen ? "zuklappen" : "aufklappen"}`}
            className="flex items-center justify-center shrink-0 rounded"
            style={{
              width: 14,
              height: 14,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
            }}
          >
            {offen ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
          </button>
        ) : (
          <span aria-hidden className="shrink-0" style={{ width: 14 }} />
        )}
        {kind && (
          <span aria-hidden className="shrink-0" style={{ color: "var(--text-muted)" }}>
            ·
          </span>
        )}
        {klickbar ? (
          <button
            type="button"
            onClick={() => onOpen(zone.key.segment, roh ?? undefined)}
            title={titel}
            className="text-xs truncate text-left"
            style={{
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: kind ? "var(--text-secondary)" : "var(--text-primary)",
              fontWeight: kind ? 400 : 500,
            }}
          >
            {anzeige}
          </button>
        ) : (
          <span className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
            {anzeige}
          </span>
        )}
        {!zone.kennzahlen.rateBelastbar && (
          <span
            className="chip shrink-0"
            style={{
              background: "var(--bg-inset)",
              color: "var(--text-muted)",
              fontSize: 9,
              padding: "1px 7px",
            }}
            title={`Ø Bestand unter ${ctx.minBasis} — Wachstum und Fluktuation bleiben leer statt zu täuschen.`}
          >
            kleine Basis
          </span>
        )}
      </span>
      <KennzahlZellen k={zone.kennzahlen} ctx={ctx} />
    </div>
  );
}

/**
 * Vergleichssicht: alle Kundensegmente inkl. Talentnetzwerk nebeneinander,
 * optional je Segment nach Unterbereich aufgegliedert (PU / Service / MTB).
 *
 * EIN Request für alles: der Schalter im Kopf schickt alle aufklappbaren
 * Codes, der Chevron genau einen. Für die Vergleichssicht wird NIE getReport
 * je Segment in einer Schleife gerufen — der Server baut je Aufruf einen
 * eigenen Point-in-time-Resolver über alle Mitarbeiter.
 */
function SegmentVergleichPanel({
  from,
  to,
  onOpenZone,
}: {
  from: string;
  to: string;
  onOpenZone: (code: string, sub?: string) => void;
}) {
  // Aufklappzustand ist bewusst transient: er beschreibt eine Leseabsicht,
  // keinen teilbaren Aufriss — der steht in ?bereich=.
  const [offen, setOffen] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [sortKey, setSortKey] = useState<SortKey>("bestand");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const segmente = useMemo(() => [...offen].sort(), [offen]);
  const aufgliederung = segmente.length > 0 ? "unterbereich" : "keine";
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["kpi-segmente", from, to, aufgliederung, segmente.join(",")],
    queryFn: () =>
      kpiApi.getSegmentVergleich({
        from,
        to,
        aufgliederung,
        ...(segmente.length > 0 ? { segmente } : {}),
      }),
    staleTime: 5 * 60_000,
    // Beim Auf- und Zuklappen bleibt die Tabelle stehen, statt zu verschwinden.
    placeholderData: keepPreviousData,
  });

  const zonen = data?.zonen ?? [];
  const aufklappbar = useMemo(
    () => zonen.filter((z) => z.hatUnterbereiche).map((z) => z.key.segment),
    [zonen],
  );
  const alleOffen = aufklappbar.length > 0 && aufklappbar.every((c) => offen.has(c));
  const maxBestand = useMemo(() => Math.max(1, ...zonen.map((z) => z.kennzahlen.bestand)), [zonen]);

  const gruppen = useMemo(() => {
    // Kundensegmente zuerst — Timms Gegenstand. Die internen Bereiche stehen
    // darunter mit eigener Zwischenüberschrift: sie verwässern den Vergleich
    // nicht, fehlen aber auch nicht (sonst risse die Summe).
    const roh = [
      {
        key: "consulting",
        titel: "Kundensegmente",
        liste: zonen.filter((z) => z.group === "consulting"),
      },
      {
        key: "intern",
        titel: "Interne Bereiche",
        liste: zonen.filter((z) => z.group === "intern"),
      },
      { key: "ohne", titel: "Ohne Zuordnung", liste: zonen.filter((z) => z.group === "ohne") },
    ];
    return roh
      .filter((g) => g.liste.length > 0)
      .map((g) => ({ ...g, liste: sortiereZonen(g.liste, sortKey, sortDir) }));
  }, [zonen, sortKey, sortDir]);

  const ctx: ZeilenKontext | null = data
    ? {
        gesamt: data.gesamt,
        maxBestand,
        minBasis: data.minBasis,
        orgHistorieVerfuegbar: data.orgHistorieVerfuegbar,
        terminationDataAvailable: data.terminationDataAvailable,
        toMonth: data.toMonth,
      }
    : null;

  function sortiereNach(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    // Zahlenspalten starten absteigend (wer ist am größten/auffälligsten),
    // die Namensspalte aufsteigend.
    setSortDir(key === "label" ? "asc" : "desc");
  }

  function toggle(code: string) {
    setOffen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <section className="card overflow-hidden mb-5" aria-labelledby="segmentvergleich-titel">
      <div
        className="flex items-start justify-between gap-3 px-4 py-3 flex-wrap"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div>
          {/* Eigene Überschrift ist zwingend: der Recruiting-Trichter weiter
              unten hat eine unabhängige Bereichslogik und würde sonst als
              Unterebene dieser Tabelle gelesen. */}
          <h2
            id="segmentvergleich-titel"
            className="text-sm font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Kundensegmente im Vergleich
          </h2>
          <p className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            Alle CU-Segmente inklusive Talentnetzwerk nebeneinander
            {data ? ` · ${fmtMonthShort(data.fromMonth)} – ${fmtMonthShort(data.toMonth)}` : ""} ·
            Segment anklicken für die Einzelsicht
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <RefreshCw
              size={11}
              aria-hidden
              className="animate-spin"
              style={{ color: "var(--text-muted)" }}
            />
          )}
          {/* Umschalter wie im Rest der Oberfläche (.segmented, ohne role —
              die Beschriftung der Knöpfe ist ihr eigener Name). Beide Zustände
              können gleichzeitig inaktiv sein: dann sind einzelne Zeilen per
              Chevron offen, aber nicht alle. */}
          <div className="segmented">
            <button
              type="button"
              data-active={offen.size === 0}
              aria-pressed={offen.size === 0}
              title="Nur die Segment-Ebene zeigen"
              onClick={() => setOffen(new Set<string>())}
            >
              Segmente
            </button>
            <button
              type="button"
              data-active={alleOffen}
              aria-pressed={alleOffen}
              disabled={aufklappbar.length === 0}
              title="Alle Segmente nach Unterbereich aufgliedern (PU, Service, MTB …)"
              onClick={() => setOffen(new Set(aufklappbar))}
            >
              Segmente + Unterbereiche
            </button>
          </div>
        </div>
      </div>

      {/* Lückenregel sichtbar machen: ohne den Hinweis liest sich ein
          rückdatiertes Bild wie eine Messung. */}
      {data && !data.orgHistorieVerfuegbar && (
        <div
          className="flex items-start gap-2 px-4 py-2"
          style={{ background: "var(--bg-subtle)", borderBottom: "1px solid var(--border)" }}
        >
          <Info
            size={12}
            aria-hidden
            className="shrink-0"
            style={{ color: "var(--text-muted)", marginTop: 2 }}
          />
          <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
            Ohne Organisations-Historie gilt die heutige Zuordnung rückwirkend; interne Wechsel
            werden nicht ausgewiesen.
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col gap-1.5 px-4 py-4" aria-label="Segmentvergleich wird geladen">
          {["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
            <div key={k} className="skeleton" style={{ height: 26 }} />
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex items-center gap-2 px-4 py-5">
          <AlertCircle size={14} aria-hidden style={{ color: "var(--risk-high)" }} />
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {error instanceof Error
              ? error.message
              : "Der Segmentvergleich konnte nicht geladen werden."}
          </p>
        </div>
      )}

      {data && ctx && (
        <>
          {/* Acht Spalten passen nicht in jedes Fenster. Ohne den Scroll-Rahmen
              schneidet das overflow-hidden der Karte die hinteren Spalten
              kommentarlos ab — dieselbe Lösung wie in der Monats-Matrix. */}
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: GRID_MIN_BREITE }}>
              <div
                className="grid items-center px-4 py-2"
                style={{
                  gridTemplateColumns: GRID_SEGMENTE,
                  gap: "0 12px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <SpaltenKopf
                  spalte={SPALTE_ZONE}
                  aktiv={sortKey === "label"}
                  dir={sortDir}
                  links
                  onSort={sortiereNach}
                />
                {SPALTEN.map((s) => (
                  <SpaltenKopf
                    key={s.key}
                    spalte={s}
                    aktiv={sortKey === s.key}
                    dir={sortDir}
                    onSort={sortiereNach}
                  />
                ))}
              </div>

              {gruppen.map((g) => (
                <div key={g.key}>
                  <p
                    className="px-4 py-1.5 text-[10px] font-medium tracking-widest uppercase"
                    style={{
                      color: "var(--text-muted)",
                      background: "var(--bg-card)",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    {g.titel}
                  </p>
                  {g.liste.map((z) => (
                    <div key={`${z.key.segment}|${z.key.unterbereich ?? ""}`}>
                      <ZonenZeile
                        zone={z}
                        ctx={ctx}
                        kind={false}
                        offen={offen.has(z.key.segment)}
                        onToggle={toggle}
                        onOpen={onOpenZone}
                      />
                      {offen.has(z.key.segment) &&
                        sortiereKinder(z.kinder, sortKey, sortDir).map((kindZone) => (
                          <ZonenZeile
                            key={`${kindZone.key.segment}|${kindZone.key.unterbereich ?? ""}|${kindZone.art}`}
                            zone={kindZone}
                            ctx={ctx}
                            kind
                            offen={false}
                            onToggle={toggle}
                            onOpen={onOpenZone}
                          />
                        ))}
                      {offen.has(z.key.segment) && z.kinder.length === 0 && !isFetching && (
                        <p
                          className="px-4 py-2 text-[11px]"
                          style={{
                            background: "var(--bg-subtle)",
                            borderBottom: "1px solid var(--border)",
                            color: "var(--text-muted)",
                            paddingLeft: 58,
                          }}
                        >
                          Für diesen Zeitraum sind keine Unterbereiche hinterlegt.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Gesamtzeile: macht die Additivität prüfbar und ist der Bezug der
              Prozentpunkt-Abweichung in der Fluktuationsspalte. */}
              <div
                className="grid items-center px-4 py-2.5"
                style={{
                  gridTemplateColumns: GRID_SEGMENTE,
                  gap: "0 12px",
                  borderTop: "1px solid var(--border-light)",
                }}
              >
                <span className="flex items-center gap-1 min-w-0">
                  <span aria-hidden className="shrink-0" style={{ width: 14 }} />
                  <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>
                    Gesamt (alle Bereiche)
                  </span>
                </span>
                <KennzahlZellen k={data.gesamt} ctx={ctx} />
              </div>
            </div>
          </div>

          <p
            className="text-[11px] px-4 py-2.5 leading-relaxed"
            style={{ color: "var(--text-muted)" }}
          >
            Bestand = Beschäftigte am Monatsende, alle Zahlen in Personen. Fluktuation = Abgänge des
            Zeitraums geteilt durch den Ø Monatsend-Bestand. Quoten bleiben ab einem Ø Bestand unter{" "}
            {data.minBasis} leer („kleine Basis"), weil sie dort an einem einzelnen Austritt hängen.
            Ein interner Wechsel ist Abgang der einen und Zugang der anderen Zone — deshalb ist Δ
            Bestand ≠ Zugänge − Abgänge. Unterbereiche mit Ø Bestand unter {data.minZellgroesse}{" "}
            stehen zusammengefasst in „Weitere Unterbereiche".
          </p>
        </>
      )}
    </section>
  );
}

// ── Personalakquise-Trichter (Greenhouse) ──────────────────────────────────────
function RecruitingPanel() {
  const [bereich, setBereich] = useState<string>("");
  const { data } = useQuery({
    queryKey: ["kpi-recruiting", bereich],
    queryFn: () => kpiApi.getRecruiting(undefined, undefined, bereich || undefined),
    staleTime: 10 * 60_000,
  });
  if (!data) return null;

  if (!data.available) {
    const msg =
      data.reason === "greenhouse_not_configured"
        ? "Greenhouse nicht verbunden (API-Key fehlt)."
        : data.reason === "not_synced"
          ? "Noch nicht synchronisiert — die Bewerbungsdaten werden beim nächsten täglichen Greenhouse-Sync geladen."
          : "Aktuell keine Recruiting-Daten verfügbar.";
    return (
      <div className="card px-4 py-4 mb-5">
        <p className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
          Personalakquise-Trichter
        </p>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {msg}
        </p>
      </div>
    );
  }

  const stages = data.stages ?? [];
  const max = Math.max(stages[0]?.reached ?? 0, 1);
  const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

  return (
    <div className="card px-4 py-4 mb-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
        <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Personalakquise-Trichter
        </p>
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          {data.from} – {data.to} · Stand {fmtDate(data.syncedAt)}
        </span>
      </div>
      {/* Bereichs-Selektor: Gesamt / TNW / Senior / Intern */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {[
          { key: "", label: "Gesamt", count: null as number | null },
          ...(data.segments ?? []).map((s) => ({ key: s.key, label: s.label, count: s.count })),
        ].map((opt) => {
          const active = bereich === opt.key;
          return (
            <button
              type="button"
              key={opt.key || "gesamt"}
              onClick={() => setBereich(opt.key)}
              className="px-2.5 py-1 rounded text-xs font-medium"
              style={{
                background: active ? "var(--accent)" : "transparent",
                color: active ? "#fff" : "var(--text-muted)",
                border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
              }}
            >
              {opt.label}
              {opt.count !== null ? ` (${opt.count})` : ""}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-1.5">
        {stages.map((s) => {
          const pct = Math.round((s.reached / max) * 100);
          return (
            <div key={s.key} className="flex items-center gap-3">
              <div className="w-40 shrink-0 text-xs" style={{ color: "var(--text-secondary)" }}>
                {s.label}
              </div>
              <div
                className="flex-1 h-6 rounded overflow-hidden"
                style={{ background: "var(--bg-hover)" }}
              >
                <div
                  className="h-full flex items-center px-2"
                  style={{
                    width: `${Math.max(pct, 6)}%`,
                    background: "var(--accent)",
                    minWidth: "2.5rem",
                  }}
                >
                  <span className="text-xs font-semibold" style={{ color: "#fff" }}>
                    {s.reached}
                  </span>
                </div>
              </div>
              <div
                className="w-16 shrink-0 text-right text-[11px]"
                style={{ color: "var(--text-muted)" }}
              >
                {s.conversionFromPrev === null ? "" : `${Math.round(s.conversionFromPrev * 100)}%`}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
        Bewerbungen der letzten 12 Monate je Trichterstufe; rechts der Übergang zur Vorstufe.
        Quelle: Greenhouse (täglicher Sync).
      </p>

      {/* Monatsmatrix (Stufen × Monate) wie in Timms Excel */}
      {(data.monthly?.length ?? 0) > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
            Monatsmatrix
          </p>
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead>
                <tr style={{ color: "var(--text-muted)" }}>
                  <th
                    className="text-left font-medium py-1.5 pr-3 sticky left-0"
                    style={{ background: "var(--bg-card)", minWidth: "10rem" }}
                  >
                    Trichterstufe
                  </th>
                  {data.monthly!.map((mp) => (
                    <th
                      key={mp.month}
                      className="text-right font-medium py-1.5 px-2 whitespace-nowrap"
                    >
                      {fmtMonthShort(mp.month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stages.map((s) => (
                  <tr key={s.key} style={{ borderTop: "1px solid var(--border)" }}>
                    <td
                      className="py-1.5 pr-3 sticky left-0"
                      style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }}
                    >
                      {s.label}
                    </td>
                    {data.monthly!.map((mp) => (
                      <td
                        key={mp.month}
                        className="py-1.5 px-2 text-right font-mono-data"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {mp.reached[s.key] ?? 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
            Bewerbungen je Monat (nach Bewerbungsdatum), die die jeweilige Stufe erreicht haben.
            Jüngste Monate sind noch unvollständig (Pipeline läuft noch).
          </p>
        </div>
      )}
    </div>
  );
}
